<script setup>
import { can } from '@/@layouts/plugins/casl'
import { cookieRef } from '@/@layouts/stores/config'
import { IncidentStatus } from '@/enums/quality/incident.status'
import AiInsightService from '@/services/quality/ai-insight.service'
import IncidentService from '@/services/quality/incident.service'
import { userAuthState } from '@core/auth'
import { useRoute, useRouter } from 'vue-router'

import AiInsightCard from '@/views/quality/incident-panel/ai/AiInsightCard.vue'
import RcaDrawer from '@/views/quality/incident-panel/Rca.vue'
import ActivityCard from '@/views/quality/incident-panel/views/ActivityCard.vue'
import ApprovalDialog from '@/views/quality/incident-panel/views/ApprovalDialog.vue'
import AttachmentsCard from '@/views/quality/incident-panel/views/AttachmentsCard.vue'
import ClassificationCard from '@/views/quality/incident-panel/views/ClassificationCard.vue'
import DecisionCard from '@/views/quality/incident-panel/views/DecisionCard.vue'
import HaiCaseCard from '@/views/quality/incident-panel/views/HaiCaseCard.vue'
import ImmediateActionCard from '@/views/quality/incident-panel/views/ImmediateActionCard.vue'
import IncidentHeader from '@/views/quality/incident-panel/views/IncidentHeader.vue'
import IncidentProgress from '@/views/quality/incident-panel/views/IncidentProgress.vue'
import InvestigationListCard from '@/views/quality/incident-panel/views/InvestigationListCard.vue'
import MobileDecisionBar from '@/views/quality/incident-panel/views/MobileDecisionBar.vue'
import NarrativeCard from '@/views/quality/incident-panel/views/NarrativeCard.vue'
import PeopleInvolvedCard from '@/views/quality/incident-panel/views/PeopleInvolvedCard.vue'
import IncidentQuestionsView from '@/views/quality/incident-panel/views/Questions.vue'
import RejectionDialog from '@/views/quality/incident-panel/views/RejectionDialog.vue'
import ReporterNotesCard from '@/views/quality/incident-panel/views/ReporterNotesCard.vue'
import ReportedByCard from '@/views/quality/incident-panel/views/ReportedByCard.vue'
import SuggestedDelegationsCard from '@/views/quality/incident-panel/views/SuggestedDelegationsCard.vue'

import { onBeforeUnmount, onMounted, watch } from 'vue'

definePage({
  meta: {
    name: "incident-view",
    subject: "quality.incident",
    action: "read",
  },
})

const route = useRoute()
const router = useRouter()

const unitId = ref(cookieRef('selectedUnit') ?? null)

const incident = ref(null)
const auth = userAuthState()

await useApi(createUrl(`/incidents/incidents/${route.params.id}`, {}), {
  afterFetch: ({ data }) => {
    incident.value = data
  },
  onFetchError: error => {
    router.push({
      name: 'invalid-url',
      params: { code: 404 },
    })
  },
})

const isAuthUserIsReporter = computed(() => {
  return incident.value?.reported_by_id === auth?.user?.id
})

const triageInsight = computed(() =>
  (incident.value?.ai_insights ?? []).find(i => i.kind === 'triage') ?? null)

const summaryInsight = computed(() =>
  (incident.value?.ai_insights ?? []).find(i => i.kind === 'summary') ?? null)

const canActOnSummary = computed(() => can('review', 'quality.incident_rca'))

// A failed run is only worth showing to someone who can retry it; everyone else sees nothing.
const showSummary = computed(() => incident.value?.incident_status === IncidentStatus.CLOSED
  && !!summaryInsight.value
  && (summaryInsight.value.status !== 'failed' || canActOnSummary.value))

// The closed-case summary replaces the triage summary at the top of the page.
const showTriage = computed(() => !!triageInsight.value && !showSummary.value)

const canActOnTriage = computed(() => can('verification', 'quality.incident')
  && [IncidentStatus.REQUEST_SUBMITTED, IncidentStatus.DELEGATION_PENDING].includes(incident.value?.incident_status))

const isTriageReady = computed(() => triageInsight.value?.status === 'ready')

// The suggested delegation is useful until it has been acted on; afterwards only the applied one stays as a record.
const showDelegations = computed(() => {
  if (!isTriageReady.value) return false

  const status = incident.value?.incident_status
  const state = triageInsight.value?.item_states?.delegation?.state ?? null

  return [IncidentStatus.REQUEST_SUBMITTED, IncidentStatus.DELEGATION_PENDING].includes(status) || state === 'applied'
})

const onInsightUpdate = fresh => {
  const list = [...(incident.value?.ai_insights ?? [])]
  const idx = list.findIndex(i => i.id === fresh.id)

  if (idx === -1) list.unshift(fresh)
  else list.splice(idx, 1, fresh)
  incident.value = { ...incident.value, ai_insights: list }
}

// Set when the analyst clicks "Use in approval"; consumed after the approval saves.
const pendingApply = ref(null)

const onUseApproval = ({ insightId, incident_type_id, event_classification_id }) => {
  if (incident.value?.incident_status !== IncidentStatus.REQUEST_SUBMITTED) return

  // Pre-fill only ids the dialog actually offers, so markApplied cannot stamp an item that was never shown.
  const optionIds = raw => (Array.isArray(raw) ? raw : (raw?.data ?? [])).map(o => o.id)
  const typeIds = optionIds(typeOfIncidentOptions.value)
  const classificationIds = optionIds(classificationOptions.value)
  const typeId = incident_type_id && typeIds.includes(incident_type_id) ? incident_type_id : null
  const classificationId = event_classification_id && classificationIds.includes(event_classification_id) ? event_classification_id : null

  approvalForm.value = {
    ...approvalForm.value,
    ...(typeId ? { incident_type_id: typeId } : {}),
    ...(classificationId ? { event_classification_id: classificationId } : {}),
  }
  pendingApply.value = {
    insightId,
    incident_type_id: typeId,
    event_classification_id: classificationId,
  }
  isApprovalDialog.value = true
}

// Approve carries every suggestion the analyst has not dismissed, so the approval
// dialog opens pre-filled and the kept ones get stamped "applied" on save.
const triageApprovalPayload = computed(() => {
  if (!isTriageReady.value) return null

  const payload = triageInsight.value.payload ?? {}
  const states = triageInsight.value.item_states ?? {}
  const ids = {}

  for (const key of ['incident_type', 'event_classification']) {
    if (payload[key]?.id && !states[key]?.state) ids[`${key}_id`] = payload[key].id
  }

  return Object.keys(ids).length ? { insightId: triageInsight.value.id, ...ids } : null
})

// Labels for the "AI suggested" chips in the approval dialog.
const triageSuggested = computed(() => (isTriageReady.value ? triageInsight.value.payload ?? {} : {}))

// Both Approve buttons (decision card, phone bar) go through here.
const onApprove = () => {
  if (triageApprovalPayload.value) onUseApproval(triageApprovalPayload.value)
  else isApprovalDialog.value = true
}

// Stamp "applied" only for suggestions the analyst actually kept in the dialog.
const markApplied = async () => {
  const pending = pendingApply.value

  pendingApply.value = null
  if (!pending) return

  const keys = []
  if (pending.incident_type_id && approvalForm.value.incident_type_id === pending.incident_type_id) keys.push('incident_type')
  if (pending.event_classification_id && approvalForm.value.event_classification_id === pending.event_classification_id) keys.push('event_classification')

  for (const key of keys) {
    try {
      onInsightUpdate(await AiInsightService.setItemState(pending.insightId, key, 'applied'))
    } catch {
      // toast shown by the service; the approval itself already succeeded
    }
  }
}

// Delegation suggestion → Delegate drawer. Stamped only after the RCA actually saves.
const rcaInitialValues = ref(null)
const pendingDelegationApply = ref(null)

const onUseDelegation = ({ insightId, title, department_id, department_label, investigator_ids, investigators, note }) => {
  if (incident.value?.incident_status !== IncidentStatus.DELEGATION_PENDING) return

  // The triage gaps ride along so the drawer can append them to the note as open questions.
  const gaps = Array.isArray(triageInsight.value?.payload?.gaps) ? triageInsight.value.payload.gaps : []

  rcaInitialValues.value = { title, department_id, department_label, note, investigators, gaps }
  pendingDelegationApply.value = { insightId, department_id, investigator_ids }
  rca.value = null
  isRcaDrawer.value = true
}

const onRcaSaved = async payload => {
  const pending = pendingDelegationApply.value

  pendingDelegationApply.value = null
  if (!pending) return

  const keptDepartment = Boolean(pending.department_id) && Number(payload?.department_id) === Number(pending.department_id)
  const suggested = (pending.investigator_ids ?? []).map(Number)
  const keptInvestigator = (payload?.assign_to ?? []).some(a => a.type === 'user' && suggested.includes(Number(a.id)))
  if (!keptDepartment && !keptInvestigator) return

  try {
    onInsightUpdate(await AiInsightService.setItemState(pending.insightId, 'delegation', 'applied'))
  } catch {
    // toast shown by the service; the delegation itself already succeeded
  }
}

const onRcaView = item => {
  router.push({
    name: 'app-quality-rca-view',
    params: { type: 'incident', id: item.id },
  })
}

const onResume = () => {
  router.push({ name: 'app-quality-entry', params: { id: incident.value.id } })
}

const {
  data: rcaListing,
  execute: fetchRcaListing,
} = await useApi(createUrl('incidents/incident-rcas', {
  query: {
    search: {
      subject_type: 'INCIDENT',
      subject_id: incident.value?.id,
    },
  },
}))

const refetchIncident = async () => {
  useApi(createUrl(`/incidents/incidents/${route.params.id}`, {}), {
    afterFetch: ({ data }) => {
      incident.value = data
    },
  })
  fetchRcaListing()
}

const rca = ref()
const isRcaDrawer = ref(false)

watch(isRcaDrawer, value => {
  if (value === false) {
    rca.value = null
    rcaInitialValues.value = null
    pendingDelegationApply.value = null
  }
}, { deep: true })

const isApprovalDialog = ref(false)
const approvalForm = ref({})
const isApproving = ref(false)

const {
  data: typeOfIncidentOptions,
} = await useApi(createUrl('/masters/incident-types', {}))

const {
  data: classificationOptions,
} = await useApi(createUrl('/masters/tags', {
  query: {
    search: {
      unit_id: unitId.value,
      tag_type: 'EVENT_CLASSIFICATION',
    },
  },
}))

// The dialog validates before emitting submit.
const submitApproval = async () => {
  isApproving.value = true

  let approved = false

  await IncidentService.approve(
    route.params.id,
    {
      unit_id: unitId.value,
      ...approvalForm.value,
    },
    data => {
      //Update data
      incident.value.code = data.code
      incident.value.incident_status = data.incident_status
      incident.value.incident_type = data.incident_type
      incident.value.event_classification = data.event_classification
      approved = true

      isApprovalDialog.value = false
    },
    error => {
      //
    },
  )

  if (approved) await markApplied()

  isApproving.value = false
}

const closeApprovalDialog = () => {
  pendingApply.value = null
  isApprovalDialog.value = false
}

const isRejectionDialog = ref(false)
const rejectionForm = ref({})
const isRejecting = ref(false)
const rejectionTypeOptions = await getEnumValue('RejectionTypes')

// The dialog validates before emitting submit.
const submitRejectionForm = async () => {
  isRejecting.value = true

  await IncidentService.reject(
    route.params.id,
    {
      unit_id: unitId.value,
      ...rejectionForm.value,
    },
    data => {
      //Update data
      incident.value.incident_status = data.incident_status
      incident.value.rejection_type = data.rejection_type
      incident.value.rejection_note = data.rejection_note

      isRejectionDialog.value = false
    },
    error => {
      //
    },
  )

  isRejecting.value = false
}

const closeRejectionDialog = () => {
  isRejectionDialog.value = false
}

const review = async () => {
  await IncidentService.review(route.params.id, {
    unit_id: unitId.value,
    incident_status: IncidentStatus.DRAFT,
  }, data => {
    //Update data
    incident.value.incident_status = data.incident_status

    // Redirection
    router.push({
      name: 'app-quality-entry',
      params: { id: incident.value.id },
    })
  }, error => {
    //
  },
  )
}

// Desktop: whichever column ends first pins to the viewport while the other keeps scrolling.
// A column taller than the viewport pins once its end comes into view instead of leaving a gap.
const leftColumn = ref(null)
const rightColumn = ref(null)

const PANE_TOP = 88
const COLUMN_GAP = 16

let resizeObserver = null

const updateStickyColumns = () => {
  const wide = window.matchMedia('(min-width: 960px)').matches

  for (const el of [leftColumn.value, rightColumn.value]) {
    if (!el) continue
    if (!wide) {
      el.style.removeProperty('--col-top')
      continue
    }

    const top = Math.min(PANE_TOP, window.innerHeight - el.offsetHeight - COLUMN_GAP)

    el.style.setProperty('--col-top', `${top}px`)
  }
}

onMounted(() => {
  resizeObserver = new ResizeObserver(updateStickyColumns)
  for (const el of [leftColumn.value, rightColumn.value]) {
    if (el) resizeObserver.observe(el)
  }
  window.addEventListener('resize', updateStickyColumns)
  updateStickyColumns()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  window.removeEventListener('resize', updateStickyColumns)
})
</script>

<template>
  <div class="quality-view incident-view">
    <div class="d-flex flex-column gap-4">
      <IncidentHeader :incident="incident" />

      <IncidentProgress
        :incident="incident"
        :rca-count="rcaListing?.length ?? 0"
      />

      <div class="incident-cols">
        <!-- Left column -->
        <div
          ref="leftColumn"
          class="incident-col"
        >
          <AiInsightCard
            v-if="showSummary"
            :insight="summaryInsight"
            :incident="incident"
            :can-act="canActOnSummary"
            aura
            @update:insight="onInsightUpdate"
          />

          <AiInsightCard
            v-if="showTriage"
            :insight="triageInsight"
            :incident="incident"
            :can-act="canActOnTriage"
            aura
            @update:insight="onInsightUpdate"
          />

          <ClassificationCard :incident="incident" />

          <HaiCaseCard
            v-if="incident.hai_case"
            :hai-case="incident.hai_case"
          />

          <NarrativeCard
            v-if="!incident.hai_case"
            :description="incident.incident_description"
          />

          <ReporterNotesCard
            v-if="incident.reporter_notes?.trim()"
            :notes="incident.reporter_notes"
          />

          <ImmediateActionCard
            v-if="incident.immediate_action"
            :action="incident.immediate_action"
          />

          <PeopleInvolvedCard
            v-if="incident.involved_parties?.length"
            :parties="incident.involved_parties"
          />

          <IncidentQuestionsView :data="incident" />

          <AttachmentsCard
            v-if="incident.attachments?.length"
            :attachments="incident.attachments"
          />
        </div>

        <!-- Right column -->
        <div
          ref="rightColumn"
          class="incident-col"
        >
          <DecisionCard
            :incident="incident"
            :insight="triageInsight"
            @resume="onResume"
            @review="review"
            @approve="onApprove"
            @reject="isRejectionDialog = true"
          />

          <SuggestedDelegationsCard
            v-if="showDelegations"
            :insight="triageInsight"
            :incident="incident"
            :can-act="canActOnTriage"
            @use-delegation="onUseDelegation"
          />

          <InvestigationListCard
            :rca-listing="rcaListing"
            :incident="incident"
            @view="onRcaView"
            @delegate="isRcaDrawer = true"
          />

          <ReportedByCard :incident="incident" />

          <ActivityCard :activities="incident.activities" />
        </div>
      </div>

      <MobileDecisionBar
        :incident="incident"
        @approve="onApprove"
        @reject="isRejectionDialog = true"
      />
    </div>
  </div>

  <RcaDrawer
    v-model:isDrawerOpen="isRcaDrawer"
    :incident="incident"
    :rca="rca"
    :initial-values="rcaInitialValues"
    @fetch-rca-listing="refetchIncident"
    @saved="onRcaSaved"
  />

  <ApprovalDialog
    v-model="isApprovalDialog"
    v-model:form="approvalForm"
    :type-options="typeOfIncidentOptions"
    :classification-options="classificationOptions"
    :suggested="triageSuggested"
    :loading="isApproving"
    @submit="submitApproval"
    @close="closeApprovalDialog"
  />

  <RejectionDialog
    v-model="isRejectionDialog"
    v-model:form="rejectionForm"
    :type-options="rejectionTypeOptions"
    :loading="isRejecting"
    @submit="submitRejectionForm"
    @close="closeRejectionDialog"
  />
</template>

<style lang="scss">
@use "@styles/quality/panel-view";
</style>

<style scoped>
.incident-cols {
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 16px;
  align-items: start;
}

.incident-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-inline-size: 0;
}

@media (min-width: 960px) {
  .incident-col {
    position: sticky;
    inset-block-start: var(--col-top, 88px);
  }
}

@media (max-width: 959px) {
  .incident-cols {
    grid-template-columns: 1fr;
  }
}
</style>
